import {
    ASK_INPUT,
    branchMode, branchText, PLAYBOOK_CONDITION_OPERATORS,
    RUN_TARGET,
    STEP_KIND,
    STEP_KIND_META,
    newId,
} from "../model/playbook-model";
import { WORKFLOW_OPTION_SLOT } from "../../action-journey-studio/common/const/settings-const";
import {
    ACTION_PROMPT,
    MESSAGE_PROMPT,
    QUESTION_PROMPT,
    START,
} from "../../action-journey-studio/common/const/settings-type";

/**
 * Compile the playbook authoring model into the runtime's `stmDefinition`.
 *
 * This is the playbook equivalent of the canvas's `transformSchema`
 * (contexts/WorkflowCanvasContextProvider.js). It targets the same output
 * contract - `{ startAt, states }`, where each state is
 * `{ type, next, stateConfig }` and `stateConfig.stateType` is what the runtime
 * dispatches on - but builds it from an ordered list rather than a node graph,
 * which makes it far simpler: the "edges" are just adjacency in the list.
 *
 * Sections are presentation only. They are flattened into a single chain, so a
 * step at the end of section 1 runs straight into the first step of section 2.
 */

// `state.type` is the canvas node type. The runtime ignores it (it dispatches on
// stateConfig.stateType/type), but the console reads it to pick a node component,
// so a compiled playbook stays openable in the canvas editor.
const NODE_TYPE = {
    START: 'StartNode',
    MESSAGE: 'messagePrompt',
    QUESTION: 'questionPrompt',
    BUTTONS: 'buttonsPrompt',
    CARDS: 'cardsPrompt',
    DOC_CAPTURE: 'docCapturePrompt',
    CODEHOOK: 'codehookAction',
    WEBHOOK: 'webhookAction',
    DOC_SEARCH: 'docSearchAction',
    AGENT_ACTION: 'agentAction',
    JOURNEY: 'journeyAction',
    LIVE_AGENT: 'liveAgentAction',
    // No canvas node produces a setVariable step; the closest existing node is
    // the AI task, which is also "assign something to a variable".
    SET_VARIABLE: 'aiTask',
};

function err(errors, stepId, message) {
    errors.push({ stepId, message });
}

/**
 * Flatten the playbook into a list of "emit units" in execution order.
 *
 * Each unit is one step plus the branch structure it needs. Doing this in a
 * separate pass keeps the emit step (below) from having to reason about nesting:
 * by the time it runs, ordering and join targets are already decided.
 */
function flatten(playbook) {
    const units = [];

    const pushSteps = (steps, joinTarget) => {
        const list = steps || [];
        list.forEach((step, index) => {
            // Where this step goes when it finishes: the next sibling, or - if it
            // is the last in its list - whatever the enclosing structure joins to.
            const fallThrough = index + 1 < list.length
                ? { kind: 'step', id: list[index + 1].id }
                : joinTarget;

            if (step.kind === STEP_KIND.IF) {
                const branches = (step.branches || []).filter(b => (b.steps || []).length > 0);
                units.push({ step, fallThrough, branches, hostStepId: list[index - 1]?.id });
                // Each branch body runs, then rejoins wherever the IF would have
                // gone - so branches converge instead of ending the workflow.
                branches.forEach(branch => pushSteps(branch.steps, fallThrough));
            } else {
                units.push({ step, fallThrough });
            }
        });
    };

    // Sections share a linear fall-through path; branch bodies keep their own hosts.
    pushSteps((playbook?.sections || []).flatMap(section => section.steps || []), null);

    return units;
}

/**
 * A short human name for the compiled state.
 *
 * The runtime shows this in traces ("Running a workflow" -> step name), and the
 * console shows it on the node. Playbook steps are not named by the author, so
 * it is derived from what the step actually does and truncated to stay readable.
 */
function deriveName(step) {
    const cfg = step.config || {};
    const first = (cfg.displayTextOptions || []).map(o => o?.displayText).find(Boolean);
    const source = first || cfg.action || cfg.inputPrompt || cfg.slot || cfg.slotToAssign
        || STEP_KIND_META[step.kind]?.verb || '';
    const text = String(source).replace(/\s+/g, ' ').trim();
    return text.length > 60 ? `${text.slice(0, 57)}...` : text;
}

/** Build the `stateConfig` for one step. Returns null if the step is unusable. */
function buildStateConfig(step, errors) {
    const cfg = step.config || {};
    const name = deriveName(step);

    switch (step.kind) {
        case STEP_KIND.SEND: {
            const options = cfg.displayTextOptions || [];
            if (options.length === 0 || !options.some(o => (o?.displayText || '').trim())) {
                err(errors, step.id, 'SEND step has no message text.');
                return null;
            }
            return {
                node: NODE_TYPE.MESSAGE,
                stateConfig: {
                    stateType: MESSAGE_PROMPT,
                    type: 'text',
                    name,
                    displayTextOptions: options,
                    lang_displayTextOptions: cfg.lang_displayTextOptions,
                },
            };
        }

        case STEP_KIND.ASK: {
            const options = cfg.displayTextOptions || [];
            if (options.length === 0 || !options.some(o => (o?.displayText || '').trim())) {
                err(errors, step.id, 'ASK step has no question text.');
                return null;
            }

            const base = {
                stateType: QUESTION_PROMPT,
                name,
                slot: cfg.slot || WORKFLOW_OPTION_SLOT,
                displayTextOptions: options,
                lang_displayTextOptions: cfg.lang_displayTextOptions,
                validationErrorDisplayTextOptions: cfg.validationErrorDisplayTextOptions || [],
                autoFillFromEntity: cfg.autoFillFromEntity !== false,
            };

            if (!cfg.slot) {
                err(errors, step.id, 'ASK step has no variable to store the answer in.');
                return null;
            }

            switch (cfg.inputType) {
                case ASK_INPUT.BUTTONS:
                    if (!(cfg.data || []).length) {
                        err(errors, step.id, 'ASK step is set to Buttons but has no options.');
                        return null;
                    }
                    return {
                        node: NODE_TYPE.BUTTONS,
                        stateConfig: { ...base, type: 'buttons', data: cfg.data, optionsTitle: cfg.optionsTitle || '' },
                    };

                case ASK_INPUT.CARDS:
                    if (!(cfg.data || []).length) {
                        err(errors, step.id, 'ASK step is set to Cards but has no cards.');
                        return null;
                    }
                    return {
                        node: NODE_TYPE.CARDS,
                        stateConfig: {
                            ...base,
                            type: 'cards',
                            data: cfg.data,
                            fallbackDescription: cfg.fallbackDescription || '',
                            fallbackActions: cfg.fallbackActions || [],
                        },
                    };

                case ASK_INPUT.DOCUMENT:
                    return {
                        node: NODE_TYPE.DOC_CAPTURE,
                        stateConfig: {
                            ...base,
                            type: 'doc_capture',
                            // A document has to arrive on this turn, so there is
                            // nothing for entity extraction to pre-fill.
                            autoFillFromEntity: false,
                            allowedFileTypes: cfg.allowedFileTypes || [],
                            extractionSchema: cfg.extractionSchema || '',
                        },
                    };

                default:
                    return { node: NODE_TYPE.QUESTION, stateConfig: { ...base, type: 'text' } };
            }
        }

        case STEP_KIND.SET: {
            if (!cfg.slotToAssign) {
                err(errors, step.id, 'SET step has no variable to assign.');
                return null;
            }
            return {
                node: NODE_TYPE.SET_VARIABLE,
                stateConfig: {
                    stateType: ACTION_PROMPT,
                    type: 'setVariable',
                    name,
                    slotToAssign: cfg.slotToAssign,
                    value: cfg.value || '',
                },
            };
        }

        case STEP_KIND.RUN: {
            switch (cfg.target) {
                case RUN_TARGET.FUNCTION:
                    if (!cfg.codehookId) {
                        err(errors, step.id, 'RUN step has no function selected.');
                        return null;
                    }
                    return {
                        node: NODE_TYPE.CODEHOOK,
                        stateConfig: {
                            stateType: ACTION_PROMPT,
                            type: 'codehook',
                            name,
                            codehookId: cfg.codehookId,
                            defaultInput: cfg.defaultInput || '{}',
                            skipNotify: cfg.skipNotify !== false,
                            // Rewording only applies to a result that is shown.
                            responseModifyByAI: cfg.skipNotify === false && !!cfg.responseModifyByAI,
                        },
                    };

                case RUN_TARGET.API: {
                    const webhook = cfg.webhookConfig || {};
                    if (!webhook.url) {
                        err(errors, step.id, 'RUN step has no API URL.');
                        return null;
                    }
                    return {
                        node: NODE_TYPE.WEBHOOK,
                        stateConfig: {
                            stateType: ACTION_PROMPT,
                            type: 'webhook',
                            name,
                            skipNotify: cfg.skipNotify !== false,
                            responseModifyByAI: cfg.skipNotify === false && !!cfg.responseModifyByAI,
                            pauseAfterExecution: !!cfg.pauseAfterExecution,
                            defaultInput: cfg.defaultInput || '{}',
                            webhookConfig: {
                                url: webhook.url,
                                method: webhook.method || 'POST',
                                requestType: webhook.requestType || 'raw',
                                // The runtime parses this with JSON.loads after
                                // interpolation, so it must stay a string.
                                requestBody: webhook.requestBody || '{}',
                                requestFormData: webhook.requestFormData || [],
                                parameters: webhook.parameters || [],
                                headers: webhook.headers || [],
                                responseMapping: webhook.responseMapping || [],
                            },
                            requestMapper: { type: 'inline_script', inlineScript: '' },
                            responseMapper: { type: 'inline_script', inlineScript: '' },
                        },
                    };
                }

                case RUN_TARGET.KNOWLEDGE:
                    if (!cfg.inputPrompt) {
                        err(errors, step.id, 'RUN step has no question for the knowledge base.');
                        return null;
                    }
                    // Reading the whole file needs to know which files - an
                    // unfiltered search has nothing to open.
                    if (cfg.readFile && !(cfg.dataSourceFiles || []).length) {
                        err(errors, step.id, 'RUN step reads the whole file but has no knowledge base selected.');
                        return null;
                    }
                    return {
                        node: NODE_TYPE.DOC_SEARCH,
                        stateConfig: {
                            stateType: ACTION_PROMPT,
                            type: 'docSearch',
                            name,
                            inputPrompt: cfg.inputPrompt,
                            systemPrompt: cfg.systemPrompt || '',
                            outputFormat: cfg.outputFormat || '',
                            slotToAssign: cfg.slotToAssign || '',
                            filterKb: cfg.filterKb || [],
                            dataSourceFiles: cfg.dataSourceFiles || [],
                            readFile: !!cfg.readFile,
                            topK: cfg.topK || 20,
                            skipNotify: cfg.skipNotify !== false,
                        },
                    };

                case RUN_TARGET.PROMPT:
                    if (!(cfg.action || '').trim()) {
                        err(errors, step.id, 'RUN step has no instruction for the agent.');
                        return null;
                    }
                    return {
                        node: NODE_TYPE.AGENT_ACTION,
                        stateConfig: {
                            stateType: ACTION_PROMPT,
                            type: 'agentAction',
                            name,
                            action: cfg.action,
                            instructions: cfg.instructions || '',
                            slotToAssign: cfg.slotToAssign || '',
                            skipNotify: cfg.skipNotify !== false,
                        },
                    };

                default:
                    err(errors, step.id, 'RUN step has nothing to run.');
                    return null;
            }
        }

        case STEP_KIND.RUN_PLAYBOOK:
            if (!cfg.journeyId) {
                err(errors, step.id, 'RUN step has no playbook selected.');
                return null;
            }
            return {
                node: NODE_TYPE.JOURNEY,
                stateConfig: {
                    stateType: ACTION_PROMPT,
                    type: 'journey',
                    name,
                    journeyId: cfg.journeyId,
                },
            };

        case STEP_KIND.HANDOFF:
            return {
                node: NODE_TYPE.LIVE_AGENT,
                stateConfig: {
                    stateType: ACTION_PROMPT,
                    type: 'liveagent',
                    name: name || 'Human Agent',
                    journeyId: '',
                },
            };

        default:
            err(errors, step.id, `Unknown step type "${step.kind}".`);
            return null;
    }
}

/**
 * Legacy compiler path, retained only for mapping previously saved debugger graphs.
 * New saves emit IF as a standalone choice; this legacy IF has no state of its own.
 *
 * The runtime branches on `stateConfig.conditions` drawn on an ordinary state
 * (see bc-tech-docs/08-workflow-schema.md 6.2), so the IF's branches are
 * attached to the step *before* it and its `next` removed - exactly what the
 * canvas does when a block has several outgoing edges. The backend then
 * LLM-compiles each prose `condition` into an `expCondition` and auto-creates
 * any variable it references, so the author only writes English.
 *
 * Choosing this over emitting a `choice` state is deliberate: `choice` compares
 * its values as strings, so a numeric test like "score greater than 700" would
 * evaluate "90" > "700" as true. The `conditions` path type-coerces instead.
 */
function attachConditions(states, unit, resolve, errors) {
    const { step, branches, fallThrough } = unit;

    if (!branches || branches.length === 0) {
        err(errors, step.id, 'IF step has no branch with any steps in it.');
        return;
    }

    const host = states[unit.hostStateId];
    if (!host) {
        err(errors, step.id, 'IF step has no step before it to branch from. Add a step above it.');
        return;
    }

    const conditions = [];
    branches.forEach(branch => {
        const condition = (branch.condition || '').trim();
        if (!condition) {
            err(errors, step.id, 'A branch has no condition written on it.');
            return;
        }
        const target = resolve({ kind: 'step', id: branch.steps[0].id });
        if (!target) return;
        conditions.push({ condition, next: target });
    });

    if (conditions.length === 0) return;

    host.stateConfig.conditions = conditions;

    // The else path. Keeping it on `next` means a state that matches nothing
    // still moves on rather than handing the turn back to the agent.
    const elseTarget = resolve(fallThrough);
    if (elseTarget) {
        host.next = elseTarget;
    } else {
        delete host.next;
    }
}

export function compilePlaybook(playbook, { legacyConditions = false } = {}) {
    const errors = [];
    const states = {};

    const units = flatten(playbook);
    const realUnits = units.filter(u => u.step.kind !== STEP_KIND.GO_TO && (!legacyConditions || u.step.kind !== STEP_KIND.IF));
    const sectionsById = new Map((playbook?.sections || []).map(section => [section.id, section]));
    const unitsById = new Map(units.map(unit => [unit.step.id, unit]));
    const jumpTargets = new Map();
    units.filter(unit => unit.step.kind === STEP_KIND.GO_TO).forEach(({ step }) => {
        const target = sectionsById.get(step.config?.sectionId);
        if (!target) {
            err(errors, step.id, 'GO TO step needs an existing section. Choose a section.');
        } else if (!target.steps?.length) {
            err(errors, step.id, 'GO TO cannot target an empty section. Add a step to the target section.');
        } else {
            jumpTargets.set(step.id, { kind: 'step', id: target.steps[0].id });
        }
    });

    // An empty playbook still compiles, to a workflow that starts and
    // immediately ends. It is not useful, but it is valid, and saving it is what
    // creates the journey record everything else writes into - adding a variable
    // to a playbook with no steps yet would otherwise fail with "journey not
    // found". The empty state is reported as a warning, not an error, so the
    // save is allowed to proceed.
    if (units.length === 0) {
        const emptyStartId = newId();
        return {
            stmDefinition: {
                startAt: emptyStartId,
                states: {
                    [emptyStartId]: {
                        stateConfig: { stateType: START, blockId: emptyStartId },
                    },
                },
            },
            errors: [],
            warnings: [{ stepId: null, message: 'This playbook has no steps yet.' }],
        };
    }

    // Assign a state id per step up front, so `next` can point at a step that
    // has not been emitted yet.
    const stateIdByStep = new Map();
    realUnits.forEach(unit => stateIdByStep.set(unit.step.id, newId()));

    /**
     * Turn a fall-through target into a state id.
     *
     * An IF has no state of its own, so a target pointing at one resolves to
     * whatever the IF would hand control to - walking forward until it finds a
     * step that was actually emitted.
     */
    const resolve = target => {
        let cursor = target;
        const seen = new Set();
        while (cursor && cursor.kind === 'step') {
            if (seen.has(cursor.id)) return null; // defensive: never loop
            seen.add(cursor.id);

            if (stateIdByStep.has(cursor.id)) return stateIdByStep.get(cursor.id);

            // bound per iteration so the find() callback does not close over the
            // loop variable as it is reassigned
            const currentId = cursor.id;
            const unit = unitsById.get(currentId);
            if (!unit) return null;
            cursor = unit.step.kind === STEP_KIND.GO_TO
                ? jumpTargets.get(currentId)
                : unit.fallThrough;
        }
        return null;
    };

    // A jump must reach an executable entry. IF is executable in new graphs;
    // legacy graphs cannot target it. Jump-only cycles remain invalid.
    jumpTargets.forEach((target, stepId) => {
        let cursor = target;
        const seen = new Set([stepId]);
        while (cursor) {
            if (seen.has(cursor.id)) {
                err(errors, stepId, 'GO TO creates a cycle with no executable steps. Choose another section.');
                break;
            }
            seen.add(cursor.id);
            const unit = unitsById.get(cursor.id);
            if (legacyConditions && unit?.step.kind === STEP_KIND.IF) {
                err(errors, stepId, 'GO TO needs a section that starts with an executable step, not IF.');
                break;
            }
            if (unit?.step.kind !== STEP_KIND.GO_TO) break;
            cursor = jumpTargets.get(cursor.id);
        }
    });
    if (errors.length) return { stmDefinition: null, errors, warnings: [] };

    // Emit the real states.
    realUnits.forEach(unit => {
        if (unit.step.kind === STEP_KIND.IF) return;
        const built = buildStateConfig(unit.step, errors);
        if (!built) return;

        const stateId = stateIdByStep.get(unit.step.id);
        const next = resolve(unit.fallThrough);

        states[stateId] = {
            type: built.node,
            ...(next ? { next } : {}),
            stateConfig: {
                ...built.stateConfig,
                // The canvas groups steps into blocks; a playbook has no blocks,
                // so each step is its own. Kept because the runtime records it on
                // the session as `runningBlock`.
                blockId: stateId,
            },
        };
    });

    // Emit standalone choices; legacy mode is only for mapping older debugger graphs.
    units.forEach(unit => {
        if (unit.step.kind !== STEP_KIND.IF) return;
        unit.hostStateId = stateIdByStep.get(unit.hostStepId);
        if (legacyConditions) {
            attachConditions(states, unit, resolve, errors);
            return;
        }
        const choices = [];
        let elseTarget = resolve(unit.fallThrough);
        const allBranches = unit.step.branches || [];
        const elseBranches = allBranches.filter(branch => branchMode(branch) === 'else');
        if (elseBranches.length > 1 || (elseBranches.length && branchMode(allBranches[allBranches.length - 1]) !== 'else')) {
            err(errors, unit.step.id, 'IF can have one ELSE branch, placed last.');
        }
        if (!allBranches.some(branch => branchMode(branch) !== 'else')) {
            err(errors, unit.step.id, 'IF needs at least one conditional branch before ELSE.');
        }
        if (!unit.branches?.length) err(errors, unit.step.id, 'IF step has no branch with any steps in it.');
        (unit.step.branches || []).forEach(branch => {
            if (!branch.steps?.length) {
                err(errors, unit.step.id, 'Each IF branch needs at least one step.');
                return;
            }
            const conditionType = branchMode(branch);
            const next = resolve({ kind: 'step', id: branch.steps[0].id });
            if (!next) return;
            if (conditionType === 'else') {
                elseTarget = next;
            } else if (conditionType === 'natural') {
                const naturalLanguage = branchText(branch).trim();
                if (!naturalLanguage) { err(errors, unit.step.id, 'A branch has no condition written on it.'); return; }
                choices.push({ id: branch.id, conditionType, naturalLanguage,
                    includeConversationHistory: branch.includeConversationHistory !== false,
                    operator: 'AND', expressionList: [], next });
            } else if (conditionType === 'expression') {
                const expressionList = branch.expressionList || [];
                const operator = branch.operator || 'AND';
                if (!['AND', 'OR'].includes(operator) || !Array.isArray(expressionList) || !expressionList.length ||
                    expressionList.some(rule => !rule || typeof rule.variable !== 'string' || !rule.variable.trim() ||
                        !PLAYBOOK_CONDITION_OPERATORS.includes(rule.operator) ||
                        (!['is empty', 'is not empty'].includes(rule.operator) &&
                            (rule.value == null || String(rule.value).trim() === '')))) {
                    err(errors, unit.step.id, 'Complete each variable, operator and value in the branch expressions.');
                    return;
                }
                choices.push({ id: branch.id, conditionType, operator,
                    expressionList: expressionList.map(rule => ({
                        variable: rule.variable, operator: rule.operator,
                        value: ['is empty', 'is not empty'].includes(rule.operator) ? '' : rule.value,
                    })), next });
            } else err(errors, unit.step.id, 'Choose natural language or expression for the branch.');
        });
        const stateId = stateIdByStep.get(unit.step.id);
        states[stateId] = { type: 'choice', stateConfig: {
            stateType: 'choice', blockId: stateId, name: 'Check conditions', choices,
            next: elseTarget,
        } };
    });

    if (Object.keys(states).length === 0) {
        return { stmDefinition: null, errors, warnings: [] };
    }

    // A start state, matching what the canvas bootstraps a new workflow with.
    const firstStateId = resolve({ kind: 'step', id: units[0].step.id });
    const startId = newId();
    states[startId] = {
        // `next` is set in both places on purpose, exactly as the canvas emits it
        // (transformSchema). The executor reads `state.next` to advance, while
        // other readers - and the start node's own settings - look at
        // `stateConfig.next`. Setting only the latter halts the machine here.
        next: firstStateId,
        stateConfig: { stateType: START, blockId: startId, next: firstStateId },
    };

    const stmDefinition = { startAt: startId, states };

    // Nothing may point at a state that does not exist - the runtime would stop
    // dead there, and the backend's slot mapping would skip everything beyond it.
    Object.values(states).forEach(state => {
        const targets = [state.next, state.stateConfig?.next]
            .concat((state.stateConfig?.conditions || []).map(c => c.next), (state.stateConfig?.choices || []).map(c => c.next))
            .filter(Boolean);
        targets.forEach(target => {
            if (!states[target]) {
                errors.push({ stepId: null, message: 'Some steps have incomplete settings. Check the highlighted steps' });
            }
        });
    });

    // Debugger-only lookup. IF now has a state; GO_TO remains authoring-only.
    // Keep this lookup outside the runtime payload.
    const stepIdByState = Object.fromEntries([...stateIdByStep].map(([stepId, stateId]) => [stateId, stepId]));
    return { stmDefinition, errors, warnings: [], stepIdByState };
}

export default compilePlaybook;
